import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ToggleSwitch } from '@components/atoms/ToggleSwitch';
import { Button } from '@components/atoms/Button';
import { Loading } from '@components/atoms/Loading';
import { useTranslation } from '@hooks/useTranslation';
import { useCurrentUser, useUpdateCurrentUser } from '@hooks/api';
import {
  useDailySiteReportAvailable,
  useDailySiteReportMutations,
  useDailySiteReportSettings,
} from '@hooks/api/dailySiteReport';
import { errorCodeOf, errorMessageFor, ineligibleReasonMessage } from '@utils/dailySiteReport/errorMessageFor';
import { TimezoneSelect } from './TimezoneSelect';
import { SendHourSelect } from './SendHourSelect';

/**
 * Sprint 33, story-169. Mounted on `/profile`, under the existing Profile Information / Change
 * Password / Language sections. Hidden entirely (not disabled) when the feature is unavailable —
 * docs/ux/story-169-design.md.
 */
export const DailySiteReportSettingsCard: FC = () => {
  const t = useTranslation('dailySiteReport');
  const history = useHistory();
  const { data: available } = useDailySiteReportAvailable();
  const { data: currentUser } = useCurrentUser();
  const { data: settings, isLoading } = useDailySiteReportSettings(available === true);
  const { updateOrganizationSettings, sendNow } = useDailySiteReportMutations();
  const { mutateAsync: updateCurrentUser, isPending: togglingEmail } = useUpdateCurrentUser({
    suppressSuccessToast: true,
    suppressErrorToast: true,
  });

  const [timezone, setTimezone] = useState<string | null>(null);
  const [sendHour, setSendHour] = useState<number>(20);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setTimezone(settings.organizationTimezone ?? null);
      setSendHour(settings.organizationSendHour ?? 20);
    }
  }, [settings]);

  if (available !== true) return null;
  if (isLoading || !settings) return <Loading center size="md" />;

  const userId = currentUser?.getSelf?.userId;
  const emailEnabled = currentUser?.getSelf?.dailyReportEmailEnabled ?? settings.emailEnabled;

  const handleToggleEmail = async (next: boolean) => {
    try {
      await updateCurrentUser({ userId, dailyReportEmailEnabled: next });
      toast.success(next ? t.toggleOnToast : t.toggleOffToast);
    } catch {
      toast.error(t.toggleErrorToast);
    }
  };

  const handleSaveSchedule = async () => {
    setFieldError(null);
    try {
      await updateOrganizationSettings.mutateAsync({
        organizationId: currentUser?.getSelf?.organizationId as string,
        dailyReportTimezone: timezone,
        dailyReportSendHour: sendHour,
      });
      toast.success(t.scheduleSavedToast);
    } catch (error: any) {
      const code = error?.response?.errors?.[0]?.extensions?.code;
      if (code === 'DAILY_SITE_REPORT_INVALID_TIMEZONE') setFieldError(t.errorInvalidTimezone);
      else if (code === 'DAILY_SITE_REPORT_INVALID_SEND_HOUR') setFieldError(t.errorInvalidSendHour);
      else toast.error(t.toggleErrorToast);
    }
  };

  const handleSendNow = async () => {
    try {
      const report = await sendNow.mutateAsync();
      history.push(`/reports/daily/${report.reportId}`);
    } catch (error: unknown) {
      // One translated toast, never the raw backend message (story-207).
      toast.error(errorMessageFor(errorCodeOf(error), t));
    }
  };

  // `eligible` is absent on an older backend: fail open, the server still checks (story-207).
  const ineligible = settings.eligible === false;
  const remaining = settings.manualSendsRemainingToday;
  const remainingLabel = (remaining === 1 ? t.sendNowRemainingOne : t.sendNowRemainingOther).replace(
    '{n}',
    String(remaining),
  );

  return (
    <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-900 rounded-xl max-w-xl" data-testid="daily-site-report-card">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{t.cardTitle}</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t.cardDescription}</p>

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-gray-900 dark:text-gray-100">{t.emailToggleLabel}</span>
        <ToggleSwitch
          testId="daily-site-report-email-toggle"
          leftLabel="Off"
          rightLabel="On"
          checked={!!emailEnabled}
          disabled={togglingEmail}
          onChange={handleToggleEmail}
        />
      </div>

      <Button
        type="button"
        variant="secondary"
        disabled={ineligible || remaining <= 0 || sendNow.isPending}
        aria-describedby={ineligible ? 'daily-site-report-ineligible' : undefined}
        onClick={handleSendNow}>
        {sendNow.isPending
          ? t.sendNowSending
          : ineligible
            ? t.sendNowButton
            : `${t.sendNowButton} (${remainingLabel})`}
      </Button>
      {ineligible && (
        <p
          id="daily-site-report-ineligible"
          data-testid="daily-site-report-ineligible"
          className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          {ineligibleReasonMessage(settings.ineligibleReason, t)}
        </p>
      )}

      {settings.canEditOrganizationSettings && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3">{t.orgSectionTitle}</h3>
          {!settings.organizationTimezone && (
            <p className="text-sm text-amber-700 dark:text-amber-400 mb-3">{t.orgDisabledBanner}</p>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">{t.timezoneLabel}</label>
              <TimezoneSelect value={timezone} onChange={setTimezone} disabled={updateOrganizationSettings.isPending} />
              {fieldError === t.errorInvalidTimezone && <p className="text-xs text-red-600 mt-1">{fieldError}</p>}
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t.sendHourLabel}</label>
              <SendHourSelect
                value={sendHour}
                timezone={timezone}
                onChange={setSendHour}
                disabled={updateOrganizationSettings.isPending}
              />
              {fieldError === t.errorInvalidSendHour && <p className="text-xs text-red-600 mt-1">{fieldError}</p>}
            </div>
          </div>
          <div className="mt-3">
            <Button type="button" disabled={updateOrganizationSettings.isPending} onClick={handleSaveSchedule}>
              {t.saveScheduleButton}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailySiteReportSettingsCard;
